import { format } from "date-fns";
import { getUserDateLocale } from "./dateLocale";
import { Temporal } from "@js-temporal/polyfill";
import { decodeHtmlCharacterReferences } from "./htmlCharacterReferences";
import { transformOutsideFencedCodeBlocks } from "./normalizeNoteMarkdown";

// Helper function to check if a string is valid JSON
function isValidJson(str: string): boolean {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
}

type SummaryField = string | readonly string[];

// Convert structured summary data to Markdown format
export function formatSummaryAsMarkdown(
  summary: Record<string, SummaryField | null> | string | null
): string {
  if (!summary) return "";

  // If it's a string, just return it (assuming it's already markdown)
  if (typeof summary === "string") {
    return summary;
  }

  if (isValidJson(JSON.stringify(summary))) {
    return Object.entries(summary)
      .filter((entry): entry is [string, SummaryField] => {
        const value = entry[1];
        return value !== null && value.length > 0;
      })
      .map(([key, value]) => {
        const body =
          typeof value === "string"
            ? value
            : value.map((item) => `- ${item}`).join("\n");
        return `## ${key}\n\n${body}`;
      })
      .join("\n\n");
  }

  return JSON.stringify(summary);
}

export const ZERO_WIDTH_SPACE = "\u200b";

/**
 * Two or more consecutive hard breaks (`\` + newline, what Plate writes for
 * Shift+Enter twice) are a blank line.
 */
export function foldBlankHardBreaks(markdown: string): string {
  return transformOutsideFencedCodeBlocks(markdown, (value) =>
    value.replace(/\\\r?\n(?:[ \t]*\\\r?\n)+/g, "\n\n")
  );
}

// Convert Markdown to plain text
export function markdownToPlainText(markdown: string): string {
  if (!markdown) return "";

  return (
    decodeHtmlCharacterReferences(foldBlankHardBreaks(markdown))
      .replace(/\\\r?\n/g, "\n")
      // Remove unwanted HTML tags first
      .replace(/<\/?u>/g, "") // Remove <u> and </u> tags (underline)
      .replace(/<\/?i>/g, "") // Remove <i> and </i> tags
      .replace(/<\/?b>/g, "") // Remove <b> and </b> tags
      .replace(/<\/?em>/g, "") // Remove <em> and </em> tags
      .replace(/<\/?strong>/g, "") // Remove <strong> and </strong> tags
      .replace(/<\/?span[^>]*>/g, "") // Remove span tags with any attributes
      .replace(/<\/?div[^>]*>/g, "") // Remove div tags with any attributes
      .replace(/<br\s*\/?>/g, "\n") // Convert <br> tags to line breaks
      .replace(/<[^>]+>/g, "") // Remove any other HTML tags
      // Remove headers
      .replace(/^[ \t]*#{1,6}[ \t]+/gm, "")
      .replace(/^[ \t]*(?:[-*+]|\d+\.)[ \t]+/gm, "• ")
      .replace(/^(?![ \t]|• )(.*\S.*)\n(?=• )/gm, "$1\n\n")
      // Remove bold and italic formatting
      .replace(/\*\*\*(.*?)\*\*\*/g, "$1") // Bold italic
      .replace(/\*\*(.*?)\*\*/g, "$1") // Bold
      .replace(/\*(.*?)\*/g, "$1") // Italic
      // Remove markdown images entirely in plain text output
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, "")
      // Remove links but keep the text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      // Remove inline code
      .replace(/`([^`]+)`/g, "$1")
      // Remove code blocks
      .replace(/```[\s\S]*?```/g, "")
      // Remove blockquotes
      .replace(/^[ \t]*>[ \t]?/gm, "")
      // Remove escape characters
      .replace(/\\(.)/g, "$1")
      // Convert double line breaks to proper paragraph spacing
      .replace(/\n\s*\n/g, "\n\n")
      // Clean up extra whitespace
      .replace(/[ \t]+/g, " ")
      // Remove leading/trailing whitespace from lines
      .replace(/^[ \t]+|[ \t]+$/gm, "")
      // Ensure proper spacing between sections
      .replace(/\n{3,}/g, "\n\n")
      .replace(new RegExp(`^${ZERO_WIDTH_SPACE}\n\n`, "gm"), "\n")
      .replaceAll(ZERO_WIDTH_SPACE, "")
      .trim()
  );
}

// Convert 24-hour time format to 12-hour format (e.g., "18:22:02" to "6:22 PM")
export function formatTimeTo12Hour(timeString: string): string {
  if (!timeString) return "";

  // Parse the time string (e.g., "18:22:02")
  const [hours, minutes] = timeString.split(":");
  const hour = parseInt(hours, 10);
  const minute = parseInt(minutes, 10);

  // Convert to 12-hour format
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

  // Format as HH:MM AM/PM
  return `${hour12}:${minute.toString().padStart(2, "0")} ${period}`;
}

export const JAN_PLACEHOLDER_REGEX =
  /^jan-image:(jan-(?:charts|generated-images)\/[^)"'\s]+)/;
export const JAN_PRESIGNED_URL_REGEX =
  /amazonaws\.com\/(jan-(?:charts|generated-images)\/[^?)"'\s]+)/;

/**
 * Convert an SVG blob/string to PNG ArrayBuffer via an offscreen canvas.
 */
/**
 * Extract S3 key from a jan-image URL. Handles both formats:
 * - jan-image:jan-charts/userId/uuid.svg  (placeholder)
 * - https://...amazonaws.com/jan-charts/userId/uuid.svg?X-Amz-...  (presigned URL)
 * Returns null if the URL is not a jan image.
 */
export function extractJanS3Key(url: string): string | null {
  // jan-image: placeholder
  const placeholderMatch = url.match(JAN_PLACEHOLDER_REGEX);
  if (placeholderMatch) return placeholderMatch[1];

  // Presigned S3 URL
  const presignedMatch = url.match(JAN_PRESIGNED_URL_REGEX);
  if (presignedMatch) return presignedMatch[1];

  return null;
}

export const formatDuration = (seconds: number) => {
  const minutes = String(Math.floor(seconds / 60)).padStart(2, "0");
  const secs = String(seconds % 60).padStart(2, "0");
  return `${minutes}:${secs}`;
};

export const formatTime = (timeString: string): string => {
  if (!timeString || typeof timeString !== "string") return "";

  try {
    // Add a dummy date to create a valid date object
    const date = new Date(`2000-01-01T${timeString}`);
    if (isNaN(date.getTime())) return "";
    return format(date, "p", { locale: getUserDateLocale() });
  } catch (error) {
    console.error("Error formatting time:", error);
    return "";
  }
};

export const formatDate = (dateString: string): string => {
  if (!dateString || typeof dateString !== "string") return "";

  try {
    // Using the Temporal API for timezone-agnostic date formatting
    const plainDate = Temporal.PlainDate.from(dateString);

    // Get the day, month, and year values for formatting
    const day = plainDate.day;
    const month = plainDate.month - 1; // JavaScript months are 0-indexed
    const year = plainDate.year;

    // Create a Date object with these values, ensuring no timezone conversion affects the day
    const dateObj = new Date(year, month, day);

    return dateObj.toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date with Temporal API:", error);
    return "";
  }
};

export const formatAttachmentDate = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return "Date unavailable";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "Date unavailable";
    }
    return format(date, "PPp", { locale: getUserDateLocale() });
  } catch (error) {
    console.warn("Error formatting attachment date:", error, { dateString });
    return "Date unavailable";
  }
};

// Export validation utilities
export { validateDob } from "./validation";
